/**
 * Página de configuração da empresa do tenant
 *
 * RELEASE: 2026-06-21 v2.0.0
 *  - Inclui cadastro do RECEBEDOR (documento, telefone, endereço) — campos
 *    obrigatórios para criar conta corrente e exibidos no link de pagamento.
 *  - Substitui SELECT/UPDATE direto em `tenants` pelas RPCs seguras
 *    `get_tenant_company_data` e `update_tenant_company_data`.
 *  - Adiciona banner de status (completo / incompleto) e auto-preenchimento
 *    de endereço por CEP via ViaCEP.
 *
 * RELEASE: 1.x — versão original (somente nome/website/email).
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Building2, Globe, Mail, Save, Loader2, Plus,
  IdCard, Phone, MapPin, CheckCircle2, AlertTriangle,
  Image as ImageIcon, Upload, Trash2,
} from 'lucide-react';
import { ListPageHeader } from '@/components/layout/list-page';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { CreateCompanyDialog } from '@/components/tenant/CreateCompanyDialog';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

// =============================================================================
// Constantes
// =============================================================================
const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB',
  'PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
] as const;

// =============================================================================
// Helpers de máscara (somente apresentação — backend recebe dígitos)
// =============================================================================
const onlyDigits = (s: string) => s.replace(/\D/g, '');

const maskCpf = (v: string) => {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};
const maskCnpj = (v: string) => {
  const d = onlyDigits(v).slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};
const maskPhone = (v: string) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};
const maskCep = (v: string) => {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
};

// =============================================================================
// Schema zod (espelha validações do backend)
// =============================================================================
const companyFormSchema = z.object({
  name: z.string().trim().min(2, 'Mínimo 2 caracteres').max(100, 'Máximo 100 caracteres'),
  website: z.string().trim().url('URL inválida').optional().or(z.literal('')),
  contact_email: z.string().trim().email('Email inválido').optional().or(z.literal('')),

  // Recebedor
  document_type: z.enum(['cpf', 'cnpj']).optional().or(z.literal('')),
  document_number: z.string().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address_cep: z.string().optional().or(z.literal('')),
  address_street: z.string().trim().max(200).optional().or(z.literal('')),
  address_number: z.string().trim().max(20).optional().or(z.literal('')),
  address_complement: z.string().trim().max(100).optional().or(z.literal('')),
  address_neighborhood: z.string().trim().max(100).optional().or(z.literal('')),
  address_city: z.string().trim().max(100).optional().or(z.literal('')),
  address_state: z.string().trim().length(2, 'UF deve ter 2 letras').optional().or(z.literal('')),
}).superRefine((data, ctx) => {
  // Documento coerente com tipo
  const doc = onlyDigits(data.document_number || '');
  if (data.document_type === 'cpf' && doc && doc.length !== 11) {
    ctx.addIssue({ code: 'custom', path: ['document_number'], message: 'CPF deve ter 11 dígitos' });
  }
  if (data.document_type === 'cnpj' && doc && doc.length !== 14) {
    ctx.addIssue({ code: 'custom', path: ['document_number'], message: 'CNPJ deve ter 14 dígitos' });
  }
  // Telefone
  const phone = onlyDigits(data.phone || '');
  if (phone && (phone.length < 10 || phone.length > 11)) {
    ctx.addIssue({ code: 'custom', path: ['phone'], message: 'Telefone deve ter 10 ou 11 dígitos' });
  }
  // CEP
  const cep = onlyDigits(data.address_cep || '');
  if (cep && cep.length !== 8) {
    ctx.addIssue({ code: 'custom', path: ['address_cep'], message: 'CEP deve ter 8 dígitos' });
  }
});

type CompanyFormData = z.infer<typeof companyFormSchema>;

// Campos obrigatórios para "cadastro completo do recebedor"
const REQUIRED_RECEIVER_FIELDS: Array<keyof CompanyFormData> = [
  'document_type', 'document_number', 'phone',
  'address_cep', 'address_street', 'address_number',
  'address_neighborhood', 'address_city', 'address_state',
];

// =============================================================================
// Componente principal
// =============================================================================
const TenantCompany = () => {
  const { currentTenant } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setShowCreateDialog(true));
  const [isLoading, setIsLoading] = useState(true);
  const [isLookingUpCep, setIsLookingUpCep] = useState(false);

  // -- Logomarca da empresa --------------------------------------------------
  // Mantida fora do form para isolar o upload (Storage) do submit (RPC).
  // `logoUrl` é a URL atualmente gravada em `tenants.logo_url`.
  // `logoDirty` indica que o usuário alterou (upload/remoção) e o submit deve
  // enviar `p_logo_url`. Sem alteração, o campo segue NULL (preserva atual).
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoDirty, setLogoDirty] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Plano efetivo híbrido (legado + integracao_v1)
  const { limits } = useResourceLimits();
  const effectivePlanId = (limits?.plan_id as string | undefined) || currentTenant?.plan;
  const isFreePlan = effectivePlanId === 'free';

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      name: '',
      website: '',
      contact_email: '',
      document_type: '' as any,
      document_number: '',
      phone: '',
      address_cep: '',
      address_street: '',
      address_number: '',
      address_complement: '',
      address_neighborhood: '',
      address_city: '',
      address_state: '',
    },
  });

  const watched = form.watch();

  // -- Carga inicial via RPC segura -------------------------------------------
  useEffect(() => {
    const load = async () => {
      if (!currentTenant?.id) { setIsLoading(false); return; }
      try {
        const { data, error } = await supabase.rpc('get_tenant_company_data', {
          p_tenant_hint: currentTenant.id,
        });
        if (error) throw error;
        const d: any = data || {};
        form.reset({
          name: d.name || '',
          website: d.website || '',
          contact_email: d.contact_email || '',
          document_type: (d.document_type as any) || '',
          document_number: d.document_type === 'cnpj'
            ? maskCnpj(d.document_number || '')
            : d.document_type === 'cpf'
              ? maskCpf(d.document_number || '')
              : (d.document_number || ''),
          phone: maskPhone(d.phone || ''),
          address_cep: maskCep(d.address_cep || ''),
          address_street: d.address_street || '',
          address_number: d.address_number || '',
          address_complement: d.address_complement || '',
          address_neighborhood: d.address_neighborhood || '',
          address_city: d.address_city || '',
          address_state: d.address_state || '',
        });
        setLogoUrl(d.logo_url || null);
        setLogoDirty(false);
      } catch (err) {
        logger.error('Company', 'Erro ao carregar dados', err);
        toast.error('Erro ao carregar dados da empresa');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [currentTenant?.id, form]);

  // -- Banner de status (completo/incompleto) --------------------------------
  const missingCount = useMemo(() => {
    return REQUIRED_RECEIVER_FIELDS.reduce((acc, f) => {
      const v = String(watched[f] || '').trim();
      // CEP/telefone/doc validamos por dígitos
      if (['address_cep', 'phone', 'document_number'].includes(f as string)) {
        return acc + (onlyDigits(v).length === 0 ? 1 : 0);
      }
      return acc + (v ? 0 : 1);
    }, 0);
  }, [watched]);
  const isComplete = missingCount === 0;

  // -- Auto-preenchimento de endereço via ViaCEP -----------------------------
  const handleCepBlur = async () => {
    const cep = onlyDigits(form.getValues('address_cep') || '');
    if (cep.length !== 8) return;
    try {
      setIsLookingUpCep(true);
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data?.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      form.setValue('address_street', data.logradouro || form.getValues('address_street'));
      form.setValue('address_neighborhood', data.bairro || form.getValues('address_neighborhood'));
      form.setValue('address_city', data.localidade || form.getValues('address_city'));
      form.setValue('address_state', (data.uf || '').toUpperCase());
      form.setValue('address_complement', data.complemento || form.getValues('address_complement'));
    } catch (err) {
      logger.error('Company', 'Falha ViaCEP', err);
    } finally {
      setIsLookingUpCep(false);
    }
  };

  // -- Upload da logomarca da empresa (bucket público `anexos`) -------------
  // Centraliza a logo no tenant. Reaproveitada por checkout, painel do
  // afiliado (fallback) e demais módulos.
  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo arquivo depois
    if (!file || !currentTenant?.id) return;

    // Validações simples no cliente (defesa em profundidade — backend valida URL)
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato inválido. Use PNG, JPG, SVG ou WEBP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 2 MB).');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `tenant-branding/${currentTenant.id}/logo.${ext}`;

      // Remove variantes anteriores para evitar arquivos órfãos.
      const variants = ['png', 'jpg', 'jpeg', 'svg', 'webp'].map(
        (e) => `tenant-branding/${currentTenant.id}/logo.${e}`,
      );
      await supabase.storage.from('anexos').remove(variants);

      const { error: upErr } = await supabase.storage
        .from('anexos')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage.from('anexos').getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      setLogoUrl(publicUrl);
      setLogoDirty(true);
      toast.success('Logo carregada. Clique em "Salvar Alterações" para confirmar.');
    } catch (err: any) {
      logger.error('Company', 'Falha no upload da logo', err);
      toast.error(err?.message || 'Falha ao enviar a logo.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoRemove = () => {
    setLogoUrl(null);
    setLogoDirty(true);
  };

  // -- Submit via RPC --------------------------------------------------------
  const onSubmit = async (data: CompanyFormData) => {
    if (!currentTenant?.id) { toast.error('Tenant não encontrado'); return; }
    setIsSaving(true);
    try {
      const { error } = await supabase.rpc('update_tenant_company_data', {
        p_name: data.name,
        p_website: data.website || null,
        p_contact_email: data.contact_email || null,
        p_document_type: (data.document_type || null) as any,
        p_document_number: data.document_number ? onlyDigits(data.document_number) : null,
        p_phone: data.phone ? onlyDigits(data.phone) : null,
        p_address_cep: data.address_cep ? onlyDigits(data.address_cep) : null,
        p_address_street: data.address_street || null,
        p_address_number: data.address_number || null,
        p_address_complement: data.address_complement || null,
        p_address_neighborhood: data.address_neighborhood || null,
        p_address_city: data.address_city || null,
        p_address_state: data.address_state ? data.address_state.toUpperCase() : null,
        // Logo: só envia se houve alteração. NULL = preserva, '' = limpa, URL = grava.
        p_logo_url: logoDirty ? (logoUrl ?? '') : (null as any),
        p_tenant_hint: currentTenant.id,
      } as any);
      if (error) throw error;
      setLogoDirty(false);
      toast.success('Alterações salvas com sucesso!');
      logger.info('Company', 'Dados salvos');
    } catch (err: any) {
      logger.error('Company', 'Erro ao salvar', err);
      toast.error(err?.message || 'Erro ao salvar alterações');
    } finally {
      setIsSaving(false);
    }
  };

  // Tipo de documento atual (afeta máscara)
  const docType = form.watch('document_type');

  return (
    <div className="space-y-6">
      {/* Header padrão do sistema (ListPageHeader) */}
      <ListPageHeader
        title="Empresa"
        description="Configure as informações da sua empresa"
        actions={
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={isFreePlan ? 'cursor-not-allowed' : ''}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateDialog(true)}
                    disabled={isFreePlan}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Nova Empresa
                  </Button>
                </div>
              </TooltipTrigger>
              {isFreePlan && (
                <TooltipContent side="bottom" className="max-w-xs p-3">
                  <p className="text-sm mb-2">Disponível apenas para planos pagos.</p>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-primary hover:underline"
                    onClick={() => navigate('/tenant/plans')}
                  >
                    Fazer upgrade →
                  </Button>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        }
      />

      {/* Banner de status do cadastro do recebedor */}
      {!isLoading && (
        isComplete ? (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">Cadastro do recebedor completo</p>
              <p className="text-muted-foreground">Os dados serão exibidos no link de pagamento.</p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">
                Cadastro do recebedor incompleto — {missingCount} {missingCount === 1 ? 'campo pendente' : 'campos pendentes'}
              </p>
              <p className="text-muted-foreground">
                Conclua o cadastro abaixo para liberar a criação de conta corrente e exibir os dados corretamente no link de pagamento.
              </p>
            </div>
          </div>
        )
      )}

      <CreateCompanyDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Grid: Dados básicos + Contato */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Card: Dados da Empresa */}
            <Card className="group relative overflow-hidden border-border/50 hover:border-border hover:shadow-md transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardHeader className="relative pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Dados da Empresa</CardTitle>
                    <CardDescription className="text-sm">Identificação e informações básicas</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Nome da Empresa</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Nome da empresa" disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="border-t border-border/50" />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Identificador</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded-md bg-muted text-sm font-mono text-muted-foreground border">
                      {currentTenant?.slug || 'slug-da-empresa'}
                    </code>
                  </div>
                  <p className="text-xs text-muted-foreground">O identificador não pode ser alterado</p>
                </div>
              </CardContent>
            </Card>

            {/* Card: Informações de Contato */}
            <Card className="group relative overflow-hidden border-border/50 hover:border-border hover:shadow-md transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardHeader className="relative pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <Globe className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Informações de Contato</CardTitle>
                    <CardDescription className="text-sm">Website e email de contato</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative space-y-4">
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Website</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input {...field} type="url" placeholder="https://www.empresa.com.br" disabled={isLoading} className="pl-10" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="border-t border-border/50" />
                <FormField
                  control={form.control}
                  name="contact_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground">Email de Contato</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input {...field} type="email" placeholder="contato@empresa.com.br" disabled={isLoading} className="pl-10" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* =================================================================
              NOVO CARD: Logomarca da Empresa (centralizada — usada por todos
              os módulos: checkout público, painel do afiliado, etc.)
              ================================================================= */}
          <Card className="group relative overflow-hidden border-border/50 hover:border-border hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <CardHeader className="relative pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <ImageIcon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Logomarca da Empresa</CardTitle>
                  <CardDescription className="text-sm">
                    Exibida no link público de pagamento e reaproveitada em todos os módulos (painel do afiliado, etc.)
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Preview */}
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 overflow-hidden">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logomarca da empresa"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                  )}
                </div>

                {/* Ações */}
                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={handleLogoFileChange}
                        disabled={isUploadingLogo || isLoading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2 cursor-pointer"
                        disabled={isUploadingLogo || isLoading}
                        asChild
                      >
                        <span>
                          {isUploadingLogo ? (
                            <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>
                          ) : (
                            <><Upload className="h-4 w-4" /> {logoUrl ? 'Trocar logo' : 'Enviar logo'}</>
                          )}
                        </span>
                      </Button>
                    </label>
                    {logoUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2 text-destructive hover:text-destructive"
                        onClick={handleLogoRemove}
                        disabled={isUploadingLogo || isLoading}
                      >
                        <Trash2 className="h-4 w-4" /> Remover
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: PNG, JPG, SVG ou WEBP — até 2 MB. Use preferencialmente uma imagem quadrada com fundo transparente.
                    {logoDirty && (
                      <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">
                        Alteração pendente — clique em "Salvar Alterações".
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>



          {/* =================================================================
              NOVO CARD: Dados do Recebedor (documento + contato + endereço)
              ================================================================= */}
          <Card className="group relative overflow-hidden border-border/50 hover:border-border hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <CardHeader className="relative pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400">
                  <IdCard className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Dados do Recebedor</CardTitle>
                  <CardDescription className="text-sm">
                    Informações exibidas no link de pagamento e obrigatórias para criar conta corrente
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="relative space-y-6">
              {/* Sub-seção: Documento */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Documento
                </h4>
                <div className="grid gap-4 md:grid-cols-[180px_1fr]">
                  <FormField
                    control={form.control}
                    name="document_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">Tipo</FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value || ''}
                            onValueChange={(v) => {
                              field.onChange(v);
                              // Reaplica máscara ao trocar tipo
                              const raw = onlyDigits(form.getValues('document_number') || '');
                              form.setValue('document_number', v === 'cnpj' ? maskCnpj(raw) : maskCpf(raw));
                            }}
                            className="flex gap-4 pt-1"
                            disabled={isLoading}
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="cpf" id="doc-cpf" />
                              <span className="text-sm">CPF</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="cnpj" id="doc-cnpj" />
                              <span className="text-sm">CNPJ</span>
                            </label>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="document_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          {docType === 'cnpj' ? 'CNPJ' : 'CPF'}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              disabled={isLoading || !docType}
                              placeholder={docType === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
                              className="pl-10"
                              onChange={(e) => {
                                const v = e.target.value;
                                field.onChange(docType === 'cnpj' ? maskCnpj(v) : maskCpf(v));
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="border-t border-border/50" />

              {/* Sub-seção: Contato */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Contato
                </h4>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="md:max-w-sm">
                      <FormLabel className="text-sm font-medium">Telefone</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            disabled={isLoading}
                            placeholder="(11) 99999-9999"
                            className="pl-10"
                            onChange={(e) => field.onChange(maskPhone(e.target.value))}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-t border-border/50" />

              {/* Sub-seção: Endereço */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" /> Endereço
                </h4>

                <div className="grid gap-4 md:grid-cols-6">
                  <FormField
                    control={form.control}
                    name="address_cep"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="text-sm font-medium">CEP</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              disabled={isLoading}
                              placeholder="00000-000"
                              onChange={(e) => field.onChange(maskCep(e.target.value))}
                              onBlur={handleCepBlur}
                            />
                            {isLookingUpCep && (
                              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_street"
                    render={({ field }) => (
                      <FormItem className="md:col-span-4">
                        <FormLabel className="text-sm font-medium">Logradouro</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={isLoading} placeholder="Rua / Avenida" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_number"
                    render={({ field }) => (
                      <FormItem className="md:col-span-1">
                        <FormLabel className="text-sm font-medium">Número</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={isLoading} placeholder="123" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_complement"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="text-sm font-medium">Complemento</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={isLoading} placeholder="Sala / Andar (opcional)" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_neighborhood"
                    render={({ field }) => (
                      <FormItem className="md:col-span-3">
                        <FormLabel className="text-sm font-medium">Bairro</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={isLoading} placeholder="Bairro" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_city"
                    render={({ field }) => (
                      <FormItem className="md:col-span-4">
                        <FormLabel className="text-sm font-medium">Cidade</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={isLoading} placeholder="Cidade" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address_state"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="text-sm font-medium">UF</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value || ''}
                            onValueChange={(v) => field.onChange(v)}
                            disabled={isLoading}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="UF" />
                            </SelectTrigger>
                            <SelectContent>
                              {UFS.map((uf) => (
                                <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botão de Salvar */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isSaving || isLoading}
              className="gap-2 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
            >
              {isSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Salvando...</>
              ) : (
                <><Save className="h-4 w-4" />Salvar Alterações</>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default TenantCompany;
